"use client";
import { useTranslations } from "next-intl";

import Link from "next/link";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { trackEducationContactEvent, type EducationContactType } from "@/lib/analytics/educationContact";
import { fetchWithAuth } from "@/lib/MemoBE/fetchWithAuth";
import Breadcrumb from "@/components/common/breadcrumb/Breadcrumb";
import { formatEducationBusinessType, formatSlugLabel, formatSlugLabels } from "@/lib/education/profileContent";

declare global {
  interface Window {
    atlas?: any;
    __azureMapsControlLoadingPromise?: Promise<void>;
  }
}

export type Centre = {
  id: string;
  slug?: string;
  name?: string;
  businessGroupId?: string;
  businessGroupName?: string;
  campusName?: string;
  isPrimaryCampus?: boolean;
  description?: string;
  shortDescription?: string;
  addressLine1?: string;
  addressLine2?: string;
  postcode?: string;
  localityName?: string;
  adminLevel1Name?: string;
  adminLevel2Name?: string;
  phone?: string;
  email?: string;
  websiteUrl?: string;
  showPhonePublicly?: boolean;
  showEmailPublicly?: boolean;
  showWebsitePublicly?: boolean;
  openingHours?: string;
  businessTypeSlug?: string;
  businessSubtypeSlug?: string;
  businessSubtypeSlugs?: string[] | null;
  city?: string;
  municipality?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  isClaimed?: boolean;
  subjectSlugs?: string[];
  deliveryModeSlugs?: string[];
  stageSlugs?: string[];
  schoolYearSlugs?: string[];
  examFocusSlugs?: string[];
  tuitionFormatSlugs?: string[] | null;
  minAge?: number | null;
  maxAge?: number | null;
  supports18Plus?: boolean | null;
};

type PublicContactRow = {
  label: string;
  value: string;
  contactType: EducationContactType;
  href?: string;
};

interface DetailMainProps {
  locale: string;
  slug: string;
  returnTo?: string | null;
  initialCentre?: Centre | null;
}

const hasText = (value?: string | null) => (value ?? "").trim().length > 0;

const toWebsiteHref = (value?: string | null) => {
  if (!hasText(value)) {
    return null;
  }

  const trimmed = value!.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const toFriendlyDetailError = (message: string, fallbackNotFound: string) => {
  const normalized = (message || "").trim();
  if (!normalized) {
    return "";
  }

  // Hide backend/problem-details noise and show a clear user-facing not-found state.
  if (/\bnot\s*found\b/i.test(normalized) || /\(404\)/.test(normalized) || /"status"\s*:\s*404/.test(normalized)) {
    return fallbackNotFound;
  }

  return normalized;
};

const publicAzureMapsKey = process.env.NEXT_PUBLIC_AZURE_MAPS_KEY;
const azureMapsScriptId = "azure-maps-control-script";
const azureMapsStyleId = "azure-maps-control-style";
const contactTelemetrySessionStorageKey = "education:contactTelemetrySessionId";
let azureMapsKeyPromise: Promise<string | null> | null = null;
const detailPageContainerStyle: React.CSSProperties = { maxWidth: "1336px" };

const createSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const escapeHtml = (value: string) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const loadAzureMapsControl = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Azure Maps can only load in the browser."));
  }

  if (window.atlas) {
    return Promise.resolve();
  }

  if (window.__azureMapsControlLoadingPromise) {
    return window.__azureMapsControlLoadingPromise;
  }

  if (!document.getElementById(azureMapsStyleId)) {
    const link = document.createElement("link");
    link.id = azureMapsStyleId;
    link.rel = "stylesheet";
    link.href = "https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css";
    document.head.appendChild(link);
  }

  window.__azureMapsControlLoadingPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(azureMapsScriptId) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Azure Maps.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = azureMapsScriptId;
    script.src = "https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Azure Maps."));
    document.head.appendChild(script);
  });

  return window.__azureMapsControlLoadingPromise;
};

const resolveAzureMapsKey = () => {
  if (publicAzureMapsKey?.trim()) {
    return Promise.resolve(publicAzureMapsKey.trim());
  }

  if (azureMapsKeyPromise) {
    return azureMapsKeyPromise;
  }

  azureMapsKeyPromise = fetch("/api/azmaps/client-key", { cache: "no-store" })
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return typeof data?.key === "string" && data.key.trim().length > 0
        ? data.key.trim()
        : null;
    })
    .catch(() => null);

  return azureMapsKeyPromise;
};

interface AzureMapsBusinessMapProps {
  latitude: number;
  longitude: number;
  label: string;
}

const AzureMapsBusinessMap: React.FC<AzureMapsBusinessMapProps> = ({ latitude, longitude, label }) => {
  const t = useTranslations("EducationClaim");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    if (!containerRef.current) {
      setMapStatus("error");
      return;
    }

    let map: any = null;
    let isCancelled = false;

    const initializeMap = async () => {
      setMapStatus("loading");

      try {
        const resolvedAzureMapsKey = await resolveAzureMapsKey();
        if (!resolvedAzureMapsKey) {
          setMapStatus("error");
          return;
        }

        await loadAzureMapsControl();

        if (isCancelled || !containerRef.current || !window.atlas) {
          return;
        }

        const atlas = window.atlas;
        map = new atlas.Map(containerRef.current, {
          center: [longitude, latitude],
          zoom: 17,
          view: "Auto",
          style: "road",
          authOptions: {
            authType: "subscriptionKey",
            subscriptionKey: resolvedAzureMapsKey,
          },
        });

        map.events.add("ready", () => {
          if (isCancelled) {
            return;
          }

          const popup = new atlas.Popup({
            content: `<div style="font-weight:600;padding:6px 8px;max-width:220px;">${escapeHtml(label)}</div>`,
            pixelOffset: [0, -32],
          });

          const marker = new atlas.HtmlMarker({
            color: "#2563eb",
            position: [longitude, latitude],
            popup,
            text: "",
          });

          map.markers.add(marker);
          map.events.add("click", marker, () => marker.togglePopup());
          marker.togglePopup();
          setMapStatus("ready");
        });
      } catch {
        if (!isCancelled) {
          setMapStatus("error");
        }
      }
    };

    void initializeMap();

    return () => {
      isCancelled = true;
      if (map) {
        map.dispose();
      }
    };
  }, [label, latitude, longitude]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{
          borderRadius: 10,
          height: 320,
          overflow: "hidden",
          width: "100%",
        }}
      />
      {mapStatus === "loading" && (
        <div
          className="text-muted small"
          style={{
            alignItems: "center",
            background: "#f8f9fb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            display: "flex",
            inset: 0,
            justifyContent: "center",
            position: "absolute",
          }}
        >
          {t("DetailMapLoading")}
        </div>
      )}
      {mapStatus === "error" && (
        <div
          className="text-muted small"
          style={{
            background: "#f8f9fb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 16,
          }}
        >
          {t("DetailMapUnavailable")}
        </div>
      )}
    </div>
  );
};

const canonicalStageOrExamLabels: Record<string, string> = {
  gcse: "GCSE",
  ks1: "KS1",
  ks2: "KS2",
  ks3: "KS3",
  sats: "SATs",
  a_level: "A-level",
  a_levels: "A-level",
  eleven_plus: "11+",
};

const formatStageOrExamLabel = (value?: string) => {
  if (!hasText(value)) {
    return "";
  }

  const normalized = value!.trim().toLowerCase().replace(/[-\s]+/g, "_");
  return canonicalStageOrExamLabels[normalized] ?? formatSlugLabel(value);
};

type OpeningHoursDayKey = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

type OpeningHoursDayRow = {
  day: OpeningHoursDayKey;
  label: string;
  slots: string[];
};

type OpeningHoursViewModel = {
  compactSummary: string | null;
  weeklyRows: OpeningHoursDayRow[] | null;
  notes: string | null;
  fallbackText: string | null;
};

const orderedOpeningHoursDays: OpeningHoursDayKey[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const weekdayLabelLocaleDates: Record<OpeningHoursDayKey, string> = {
  monday: "2026-01-05",
  tuesday: "2026-01-06",
  wednesday: "2026-01-07",
  thursday: "2026-01-08",
  friday: "2026-01-09",
  saturday: "2026-01-10",
  sunday: "2026-01-11",
};

const fallbackWeekdayLabels: Record<OpeningHoursDayKey, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const toWeekdayLabel = (day: OpeningHoursDayKey, locale: string) => {
  const isoDate = weekdayLabelLocaleDates[day];
  const utcDate = new Date(`${isoDate}T12:00:00Z`);

  try {
    const label = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(utcDate).trim();
    if (label.length > 0) {
      return `${label[0].toUpperCase()}${label.slice(1)}`;
    }
  } catch {
    // Ignore Intl formatting errors and use fallback labels.
  }

  return fallbackWeekdayLabels[day];
};

const toOpeningHoursFallbackText = (raw: string, t: ReturnType<typeof useTranslations>) => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed) as { openNow?: unknown; status?: unknown };
    if (typeof parsed?.status === "string" && parsed.status.trim().length > 0) {
      return parsed.status.trim();
    }

    if (typeof parsed?.openNow === "boolean") {
      return parsed.openNow ? t("DetailOpeningHoursOpenNow") : t("DetailOpeningHoursClosedNow");
    }

    return null;
  } catch {
    return trimmed;
  }
};

const toOpeningHoursViewModel = (
  openingHours: string | undefined,
  locale: string,
  t: ReturnType<typeof useTranslations>
): OpeningHoursViewModel | null => {
  const raw = openingHours?.trim();
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as {
      format?: unknown;
      weeklyTemplate?: Record<string, unknown>;
      notes?: { global?: unknown };
    };

    const isWeeklyTemplate = parsed?.format === "weekly-template-v1" && parsed?.weeklyTemplate && typeof parsed.weeklyTemplate === "object";
    if (!isWeeklyTemplate) {
      const fallbackText = toOpeningHoursFallbackText(raw, t);
      if (!fallbackText) {
        return null;
      }

      return {
        compactSummary: fallbackText,
        weeklyRows: null,
        notes: null,
        fallbackText,
      };
    }

    const weeklyRows: OpeningHoursDayRow[] = orderedOpeningHoursDays.map((day) => {
      const dayRangesRaw = parsed.weeklyTemplate?.[day];
      const dayRanges = Array.isArray(dayRangesRaw) ? dayRangesRaw : [];

      const slots = dayRanges
        .map((range) => {
          if (!range || typeof range !== "object") {
            return null;
          }

          const candidate = range as { start?: unknown; end?: unknown };
          const start = typeof candidate.start === "string" ? candidate.start.trim() : "";
          const end = typeof candidate.end === "string" ? candidate.end.trim() : "";
          if (!start || !end) {
            return null;
          }

          return `${start}-${end}`;
        })
        .filter((value): value is string => Boolean(value));

      return {
        day,
        label: toWeekdayLabel(day, locale),
        slots,
      };
    });

    const hasAnySlot = weeklyRows.some((row) => row.slots.length > 0);
    const notes = typeof parsed.notes?.global === "string" && parsed.notes.global.trim().length > 0
      ? parsed.notes.global.trim()
      : null;

    if (!hasAnySlot && !notes) {
      return null;
    }

    const firstOpenDay = weeklyRows.find((row) => row.slots.length > 0) || null;
    const compactSummary = firstOpenDay
      ? `${firstOpenDay.label}: ${firstOpenDay.slots.join(", ")}`
      : notes;

    return {
      compactSummary,
      weeklyRows,
      notes,
      fallbackText: null,
    };
  } catch {
    const fallbackText = toOpeningHoursFallbackText(raw, t);
    if (!fallbackText) {
      return null;
    }

    return {
      compactSummary: fallbackText,
      weeklyRows: null,
      notes: null,
      fallbackText,
    };
  }
};

const DetailMain: React.FC<DetailMainProps> = ({ locale, slug, returnTo = null, initialCentre = null }) => {
  const t = useTranslations("EducationClaim");
  const [centre, setCentre] = useState<Centre | null>(initialCentre);
  const [slugCanonicalFromHeader, setSlugCanonicalFromHeader] = useState<string | null>(null);
  const [relatedCampuses, setRelatedCampuses] = useState<Centre[]>([]);
  const [revealedPhoneValue, setRevealedPhoneValue] = useState<string | null>(null);
  const [revealedEmailValue, setRevealedEmailValue] = useState<string | null>(null);
  const [isRevealingPhone, setIsRevealingPhone] = useState(false);
  const [isRevealingEmail, setIsRevealingEmail] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [isLoading, setIsLoading] = useState(!initialCentre);
  const [error, setError] = useState<string | null>(null);
  const contactImpressionTrackingKeyRef = useRef<string | null>(null);
  const contactSessionIdRef = useRef<string | null>(null);
  const sortedRelatedCampuses = useMemo(
    () => [...relatedCampuses].sort((left, right) => {
      if ((left.isPrimaryCampus === true) !== (right.isPrimaryCampus === true)) {
        return left.isPrimaryCampus === true ? -1 : 1;
      }

      const leftCampus = (left.campusName || left.name || left.id || "").trim().toLowerCase();
      const rightCampus = (right.campusName || right.name || right.id || "").trim().toLowerCase();
      return leftCampus.localeCompare(rightCampus);
    }),
    [relatedCampuses]
  );
  const safeReturnTo = returnTo && returnTo.startsWith("/") ? returnTo : null;
  const searchResultsHref = safeReturnTo || `/${locale}/education`;
  const claimHref = safeReturnTo
    ? `/${locale}/education/${encodeURIComponent(slug)}/claim-benefits?returnTo=${encodeURIComponent(safeReturnTo)}`
    : `/${locale}/education/${encodeURIComponent(slug)}/claim-benefits`;

  const getContactSessionId = () => {
    if (contactSessionIdRef.current) {
      return contactSessionIdRef.current;
    }

    if (typeof window === "undefined") {
      const generated = createSessionId();
      contactSessionIdRef.current = generated;
      return generated;
    }

    try {
      const existing = window.sessionStorage.getItem(contactTelemetrySessionStorageKey);
      if (existing && existing.trim().length > 0) {
        contactSessionIdRef.current = existing;
        return existing;
      }

      const generated = createSessionId();
      window.sessionStorage.setItem(contactTelemetrySessionStorageKey, generated);
      contactSessionIdRef.current = generated;
      return generated;
    } catch {
      const generated = createSessionId();
      contactSessionIdRef.current = generated;
      return generated;
    }
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(!initialCentre);
      setError(null);
      setSlugCanonicalFromHeader(null);
      setRevealedPhoneValue(null);
      setRevealedEmailValue(null);
      setIsRevealingPhone(false);
      setIsRevealingEmail(false);
      setPhoneRevealed(false);
      setEmailRevealed(false);
      contactImpressionTrackingKeyRef.current = null;

      try {
        const response = await fetchWithAuth(
          `/api/education/${encodeURIComponent(slug)}`,
          "GET",
          undefined,
          true,
          false,
          true
        ) as Response;

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || t("DetailErrorFailedToLoadProfile"));
        }

        const loadedCentre = data as Centre;
        setCentre(loadedCentre);
        const canonicalSlug = (response.headers?.get?.("x-education-canonical-slug") || "").trim();
          setSlugCanonicalFromHeader(canonicalSlug || null);

        if ((loadedCentre.businessGroupId || "").trim()) {
          const relatedResponse = await fetch(`/api/education/${encodeURIComponent(slug)}/related-campuses`, {
            method: "GET",
            cache: "no-store",
          });

          if (relatedResponse.ok) {
            const relatedData = await relatedResponse.json();
            setRelatedCampuses(Array.isArray(relatedData) ? relatedData as Centre[] : []);
          } else {
            setRelatedCampuses([]);
          }
        } else {
          setRelatedCampuses([]);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        const fallbackNotFound = t("DetailErrorCentreNotFound");
        const friendlyMessage = toFriendlyDetailError(message, fallbackNotFound);
        setError(friendlyMessage || t("DetailErrorFailedToLoadProfileWithPeriod"));
      } finally {
        setIsLoading(false);
      }
    };

    if (slug?.trim()) {
      void load();
    } else {
      setError(t("DetailErrorMissingSlug"));
      setIsLoading(false);
    }
  }, [slug, initialCentre]);

  useEffect(() => {
    if (!centre) {
      return;
    }

    const slugRequested = slug.trim();
    const dedupeKey = `${centre.id || ""}:${slugRequested.toLowerCase()}`;
    const hasPhoneCta = Boolean(centre.showPhonePublicly);
    const hasEmailCta = Boolean(centre.showEmailPublicly);
    const hasWebsiteCta = Boolean(centre.showWebsitePublicly && hasText(centre.websiteUrl));
    const hasAnyCta = hasPhoneCta || hasEmailCta || hasWebsiteCta;

    const metadata = {
      slugRequested,
      slugCanonicalFromHeader,
      centreId: centre.id || null,
      sessionId: getContactSessionId(),
      locale,
      isClaimed: centre.isClaimed ?? null,
      hasPhoneCta,
      hasEmailCta,
      hasWebsiteCta,
      placement: "provider_detail_top" as const,
    };

    const unavailableKey = `unavailable:${dedupeKey}`;
    const impressionKey = `impression:${dedupeKey}`;

    if (!hasAnyCta) {
      if (contactImpressionTrackingKeyRef.current === unavailableKey) {
        return;
      }

      contactImpressionTrackingKeyRef.current = unavailableKey;
      void trackEducationContactEvent("tc_contact_unavailable_shown", metadata);
      return;
    }

    const hasVisibleCta = hasWebsiteCta || (hasPhoneCta && phoneRevealed) || (hasEmailCta && emailRevealed);
    if (hasVisibleCta) {
      if (contactImpressionTrackingKeyRef.current === impressionKey) {
        return;
      }

      contactImpressionTrackingKeyRef.current = impressionKey;
      void trackEducationContactEvent("tc_contact_cta_impression", metadata);
    }
  }, [centre, emailRevealed, locale, phoneRevealed, slug, slugCanonicalFromHeader]);

  const trackContactClicked = (contactType: EducationContactType) => {
    if (!centre) {
      return;
    }

    const hasPhoneCta = Boolean(centre.showPhonePublicly);
    const hasEmailCta = Boolean(centre.showEmailPublicly);
    const hasWebsiteCta = Boolean(centre.showWebsitePublicly && hasText(centre.websiteUrl));

    void trackEducationContactEvent("tc_contact_cta_clicked", {
      slugRequested: slug.trim(),
      slugCanonicalFromHeader,
      centreId: centre.id || null,
      sessionId: getContactSessionId(),
      contactType,
      locale,
      isClaimed: centre.isClaimed ?? null,
      hasPhoneCta,
      hasEmailCta,
      hasWebsiteCta,
      placement: "provider_detail_top",
    });
  };

  const copyContactToClipboard = async (value: string, contactType: EducationContactType) => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      }
    } catch {
      // Ignore clipboard failures to keep the action best-effort.
    } finally {
      trackContactClicked(contactType);
    }
  };

  const revealContactValue = async (contactType: "phone" | "email") => {
    if (!slug?.trim()) {
      return;
    }

    if (contactType === "phone") {
      if (phoneRevealed || isRevealingPhone) {
        return;
      }

      setIsRevealingPhone(true);
    } else {
      if (emailRevealed || isRevealingEmail) {
        return;
      }

      setIsRevealingEmail(true);
    }

    try {
      const response = await fetch(
        `/api/education/${encodeURIComponent(slug)}/contact?type=${contactType}`,
        {
          method: "GET",
          cache: "no-store",
        }
      );

      const payload = await response.json().catch(() => null) as { value?: string } | null;
      const value = hasText(payload?.value) ? payload!.value!.trim() : null;

      if (!response.ok || !value) {
        return;
      }

      if (contactType === "phone") {
        setRevealedPhoneValue(value);
        setPhoneRevealed(true);
        return;
      }

      setRevealedEmailValue(value);
      setEmailRevealed(true);
    } catch {
      // Keep reveal as best-effort and avoid blocking the rest of the page.
    } finally {
      if (contactType === "phone") {
        setIsRevealingPhone(false);
      } else {
        setIsRevealingEmail(false);
      }
    }
  };

  if (isLoading) {
    return (
      <section className="course__area pt-40 pb-80">
        <div className="classBase container" style={detailPageContainerStyle}>{t("DetailLoadingProfile")}</div>
      </section>
    );
  }

  if (error || !centre) {
    return (
      <section className="course__area pt-40 pb-80">
        <div className="classBase container" style={detailPageContainerStyle}>
          <div className="alert alert-danger">{error || t("DetailErrorCentreNotFound")}</div>
          <Link href={`/${locale}/education`} className="btn btn-outline-primary">
            {t("DetailBackToSearchButton")}
          </Link>
        </div>
      </section>
    );
  }

  const aboutText = hasText(centre.description)
    ? centre.description!.trim()
    : hasText(centre.shortDescription)
      ? centre.shortDescription!.trim()
      : null;
  const shortDescription = (centre.shortDescription ?? "").trim();
  const hasDistinctSummary = shortDescription.length > 0
    && shortDescription.replace(/\s+/g, " ").toLowerCase() !== (aboutText ?? "").replace(/\s+/g, " ").toLowerCase();

  const hasPhoneCta = Boolean(centre.showPhonePublicly);
  const hasEmailCta = Boolean(centre.showEmailPublicly);

  const publicPhone = hasPhoneCta && hasText(revealedPhoneValue)
    ? {
      label: t("DetailContactPhoneLabel"),
      value: revealedPhoneValue!.trim(),
      href: `tel:${revealedPhoneValue!.trim()}`,
      contactType: "phone" as const,
    }
    : null;

  const publicEmail = hasEmailCta && hasText(revealedEmailValue)
    ? {
      label: t("DetailContactEmailLabel"),
      value: revealedEmailValue!.trim(),
      contactType: "email" as const,
    }
    : null;

  const websiteValue = hasText(centre.websiteUrl) ? centre.websiteUrl!.trim() : null;
  const websiteHref = toWebsiteHref(websiteValue);

  const publicWebsite = centre.showWebsitePublicly && websiteValue && websiteHref
    ? {
      label: t("DetailContactWebsiteLabel"),
      value: websiteValue,
      href: websiteHref,
      contactType: "website" as const,
    }
    : null;

  const hasAnyContactCta = hasPhoneCta || hasEmailCta || Boolean(publicWebsite);

  const subjectLabels = formatSlugLabels(centre.subjectSlugs);
  const deliveryModeLabels = formatSlugLabels(centre.deliveryModeSlugs);
  const tuitionFormatLabels = formatSlugLabels(centre.tuitionFormatSlugs);
  const examLabels = formatSlugLabels(centre.examFocusSlugs, formatStageOrExamLabel);
  const stageLabels = formatSlugLabels(centre.stageSlugs, formatStageOrExamLabel);
  const schoolYearLabels = formatSlugLabels(centre.schoolYearSlugs, formatStageOrExamLabel);
  const businessTypeDisplay = formatEducationBusinessType(centre, "-");
  const ageLabels: string[] = [];
  const { minAge, maxAge } = centre;
  const hasMinAge = typeof minAge === "number" && Number.isInteger(minAge) && minAge >= 0 && minAge <= 120;
  const hasMaxAge = typeof maxAge === "number" && Number.isInteger(maxAge) && maxAge >= 0 && maxAge <= 120;
  const invalidAgeRange = (minAge != null && !hasMinAge) || (maxAge != null && !hasMaxAge)
    || (hasMinAge && hasMaxAge && minAge > maxAge);

  if (invalidAgeRange) {
    ageLabels.push(t("DetailAgeNeedsConfirmation"));
  } else if (hasMinAge && hasMaxAge) {
    // The profile editor uses maxAge 120 for an open-ended adult age range.
    if (centre.supports18Plus === true && maxAge === 120) {
      ageLabels.push(t("DetailAgeFrom", { age: minAge }));
    } else if (minAge === maxAge) {
      ageLabels.push(t("DetailAgeSingle", { age: minAge }));
    } else {
      ageLabels.push(t("DetailAgeRange", { minAge, maxAge }));
    }
  } else if (hasMinAge) {
    ageLabels.push(t("DetailAgeMinimum", { age: minAge }));
  } else if (hasMaxAge && !(centre.supports18Plus === true && maxAge === 120)) {
    ageLabels.push(t("DetailAgeMaximum", { age: maxAge }));
  }
  if (centre.supports18Plus === true) ageLabels.push(t("DetailAdultLearners"));
  const openingHoursViewModel = toOpeningHoursViewModel(centre.openingHours, locale, t);
  const hasOpeningHoursSection = Boolean(
    openingHoursViewModel
    && (
      openingHoursViewModel.weeklyRows
      || hasText(openingHoursViewModel.fallbackText)
      || hasText(openingHoursViewModel.notes)
    )
  );
  const businessName = hasText(centre.businessGroupName)
    ? centre.businessGroupName!.trim()
    : hasText(centre.name)
      ? centre.name!.trim()
      : t("DetailFallbackCentreName");
  const campusLabel = hasText(centre.campusName)
    ? `${centre.campusName!.trim()} ${t("DetailCampusLabelSuffix")}${centre.isPrimaryCampus ? ` • ${t("DetailPrimaryCampusSuffix")}` : ""}`
    : null;

  const fullAddressParts = [
    centre.addressLine1,
    centre.addressLine2,
    centre.localityName,
    centre.city,
    centre.municipality,
    centre.adminLevel2Name,
    centre.adminLevel1Name,
    centre.postcode,
    centre.countryCode,
  ]
    .filter((value): value is string => hasText(value))
    .map((value) => value.trim());

  const dedupedAddressParts = fullAddressParts.filter((part, index, values) =>
    values.findIndex((candidate) => candidate.toLowerCase() === part.toLowerCase()) === index
  );

  const fullAddress = dedupedAddressParts.join(", ");

  const mapQuery = [centre.name, fullAddress]
    .filter((value): value is string => hasText(value))
    .map((value) => value.trim())
    .join(", ");

  const hasCoordinates = typeof centre.latitude === "number" && typeof centre.longitude === "number";
  const hasLocationMap = mapQuery.length > 0 || hasCoordinates;
  const encodedMapQuery = encodeURIComponent(mapQuery);
  const lat = hasCoordinates ? centre.latitude! : null;
  const lon = hasCoordinates ? centre.longitude! : null;
  const pinLabelRaw = (centre.name || t("DetailFallbackCentreName")).trim();
  const pinLabel = encodeURIComponent(pinLabelRaw);
  const pinSpec = hasCoordinates ? `point.${lat}_${lon}_${pinLabel}` : "";
  const pushpinSpec = hasCoordinates ? `${lat},${lon};66;${pinLabelRaw}` : "";
  const pinSearchQuery = hasCoordinates
    ? [pinLabelRaw, `${lat},${lon}`, fullAddress].filter((value) => value.trim().length > 0).join(" ")
    : mapQuery;
  const encodedFullAddress = encodeURIComponent(fullAddress);
  const googleSearchQuery = hasCoordinates
    ? [pinLabelRaw, `${lat},${lon}`, mapQuery].filter((value) => value.trim().length > 0).join(" ")
    : mapQuery;

  const bingMapHref = hasCoordinates
    ? `https://www.bing.com/maps?cp=${lat}~${lon}&lvl=18&sty=r&q=${encodeURIComponent(pinSearchQuery)}${fullAddress ? `&where1=${encodedFullAddress}` : ""}&sp=${pinSpec}&pp=${encodeURIComponent(pushpinSpec)}`
    : `https://www.bing.com/maps?q=${encodedMapQuery}`;

  const bingMapEmbedHref = hasCoordinates
    ? `https://www.bing.com/maps/embed?h=320&w=500&cp=${lat}~${lon}&lvl=18&sty=r&q=${encodeURIComponent(pinSearchQuery)}${fullAddress ? `&where1=${encodedFullAddress}` : ""}&sp=${pinSpec}&pp=${encodeURIComponent(pushpinSpec)}`
    : `https://www.bing.com/maps/embed?q=${encodedMapQuery}`;

  const googleMapHref = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleSearchQuery)}`
    : `https://www.google.com/maps/search/?api=1&query=${encodedMapQuery}`;

  const hasAnyDetailsSection =
    Boolean(aboutText)
    || hasAnyContactCta
    || hasOpeningHoursSection
    || subjectLabels.length > 0
    || deliveryModeLabels.length > 0
    || examLabels.length > 0
    || stageLabels.length > 0
    || schoolYearLabels.length > 0
    || tuitionFormatLabels.length > 0
    || ageLabels.length > 0
    || hasLocationMap
    || sortedRelatedCampuses.length > 0;

  return (
    <>
      <Breadcrumb title={businessName} backButtonUrl={searchResultsHref} />
      <section className="course__area pt-40 pb-80">
        <div
          className="classBase container"
          style={{
            ...detailPageContainerStyle,
            paddingBottom: !centre.isClaimed ? 96 : undefined,
          }}
        >
          <h1 className="visually-hidden">{businessName}</h1>
          {hasDistinctSummary && <p className="lead">{shortDescription}</p>}

        {aboutText && (
          <div className="card mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <h5>{t("DetailAboutTitle")}</h5>
              <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{aboutText}</p>
            </div>
          </div>
        )}

        <div className="row g-3 mb-4">
          <div className="col-md-6 col-lg-4">
            <div className="card h-100" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <h6>{t("DetailBusinessOverviewTitle")}</h6>
                {campusLabel && <p className="text-muted mb-1">{campusLabel}</p>}
                <p className="text-muted mb-1">{businessTypeDisplay}</p>
                {publicWebsite && (
                  <p className="text-muted mb-0">
                    <a
                      className="text-primary text-decoration-underline"
                      href={publicWebsite.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackContactClicked(publicWebsite.contactType)}
                    >
                      {publicWebsite.label}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {subjectLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailSubjectsTitle")}</h6>
                  <p className="mb-0">{subjectLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}

          {examLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailExamTypesTitle")}</h6>
                  <p className="mb-0">{examLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}

          {stageLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailStagesTitle")}</h6>
                  <p className="mb-0">{stageLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}

          {schoolYearLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailSchoolYearsTitle")}</h6>
                  <p className="mb-0">{schoolYearLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}

          {ageLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailAgesTitle")}</h6>
                  {ageLabels.map((label) => <p key={label} className="mb-0">{label}</p>)}
                </div>
              </div>
            </div>
          )}

          {tuitionFormatLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailTuitionFormatsTitle")}</h6>
                  <p className="mb-0">{tuitionFormatLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}

          {deliveryModeLabels.length > 0 && (
            <div className="col-md-6 col-lg-4">
              <div className="card h-100" style={{ borderRadius: 12 }}>
                <div className="card-body">
                  <h6>{t("DetailDeliveryModesTitle")}</h6>
                  <p className="mb-0">{deliveryModeLabels.join(", ")}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {hasLocationMap && (
          <div className="card mt-4" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <h5 className="mb-1">{t("DetailLocationMapTitle")}</h5>
                  <p className="text-muted mb-0">{fullAddress || t("DetailLocationNotProvided")}</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <a className="btn btn-sm btn-primary" href={bingMapHref} target="_blank" rel="noreferrer">
                    {t("DetailOpenInBingMapsButton")}
                  </a>
                  <a className="btn btn-sm btn-outline-primary" href={googleMapHref} target="_blank" rel="noreferrer">
                    {t("DetailOpenInGoogleMapsButton")}
                  </a>
                </div>
              </div>
              {hasCoordinates && lat !== null && lon !== null ? (
                <AzureMapsBusinessMap latitude={lat} longitude={lon} label={pinLabelRaw} />
              ) : (
                <iframe
                  title={t("DetailLocationMapIframeTitle")}
                  src={bingMapEmbedHref}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{
                    border: 0,
                    borderRadius: 10,
                    height: 320,
                    width: "100%",
                  }}
                />
              )}
            </div>
          </div>
        )}

        {hasOpeningHoursSection && openingHoursViewModel && (
          <div className="card mt-4" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <h5>{t("DetailOpeningHoursTitle")}</h5>
              {openingHoursViewModel.weeklyRows ? (
                <>
                  <ul className="list-unstyled mb-0">
                    {openingHoursViewModel.weeklyRows.map((row) => (
                      <li key={row.day} className="d-flex justify-content-between align-items-start gap-3 py-1 border-bottom">
                        <span className="fw-semibold">{row.label}</span>
                        <span>{row.slots.length > 0 ? row.slots.join(", ") : t("DetailOpeningHoursClosed")}</span>
                      </li>
                    ))}
                  </ul>
                  {openingHoursViewModel.notes && (
                    <p className="text-muted mb-0 mt-3">
                      <strong>{t("DetailOpeningHoursNotesLabel")}: </strong>
                      {openingHoursViewModel.notes}
                    </p>
                  )}
                </>
              ) : (
                <p className="mb-0">{openingHoursViewModel.fallbackText}</p>
              )}
            </div>
          </div>
        )}

        <div className="card mt-4" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <h5>{t("DetailContactTitle")}</h5>
            {hasAnyContactCta ? (
              <ul className="mb-0">
                {publicWebsite && (
                  <li key={`${publicWebsite.contactType}:${publicWebsite.value}`}>
                    {publicWebsite.label}: <a className="text-primary text-decoration-underline" href={publicWebsite.href} target="_blank" rel="noreferrer" onClick={() => trackContactClicked(publicWebsite.contactType)}>{publicWebsite.value}</a>
                  </li>
                )}

                {hasPhoneCta && (
                  <li key="phone-contact">
                    {t("DetailContactPhoneLabel")}: {phoneRevealed && publicPhone ? (
                      <>
                        <a href={publicPhone.href} onClick={() => trackContactClicked(publicPhone.contactType)}>{publicPhone.value}</a>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ms-2 py-0 px-2"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => {
                            void copyContactToClipboard(publicPhone.value, "phone");
                          }}
                        >
                          {t("DetailCopyButton")}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-2"
                        style={{ fontSize: "0.8rem" }}
                        onClick={() => {
                          void revealContactValue("phone");
                        }}
                        disabled={isRevealingPhone}
                      >
                        {t("DetailRevealPhoneButton")}
                      </button>
                    )}
                  </li>
                )}

                {hasEmailCta && (
                  <li key="email-contact">
                    {t("DetailContactEmailLabel")}: {emailRevealed && publicEmail ? (
                      <>
                        <span>{publicEmail.value}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ms-2 py-0 px-2"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => {
                            void copyContactToClipboard(publicEmail.value, "email");
                          }}
                        >
                          {t("DetailCopyButton")}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-2"
                        style={{ fontSize: "0.8rem" }}
                        onClick={() => {
                          void revealContactValue("email");
                        }}
                        disabled={isRevealingEmail}
                      >
                        {t("DetailRevealEmailButton")}
                      </button>
                    )}
                  </li>
                )}
              </ul>
            ) : (
              <p className="mb-0 text-muted">{t("DetailContactNotAvailable")}</p>
            )}
          </div>
        </div>

        {sortedRelatedCampuses.length > 0 && (
          <div className="card mt-4" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <h5 className="mb-3">{t("DetailOtherCampusesTitle")}</h5>
              <div className="d-flex flex-column gap-2">
                {sortedRelatedCampuses.map((relatedCampus) => {
                  const relatedCampusName = hasText(relatedCampus.campusName)
                    ? `${relatedCampus.campusName!.trim()} ${t("DetailCampusLabelSuffix")}`
                    : (relatedCampus.name || t("DetailFallbackCentreName"));
                  const relatedLocation = [relatedCampus.localityName || relatedCampus.city, relatedCampus.adminLevel2Name || relatedCampus.municipality]
                    .filter((value): value is string => hasText(value))
                    .map((value) => value.trim())
                    .join(" • ");
                  const relatedSlug = (relatedCampus.slug || relatedCampus.id || "").trim();

                  if (!relatedSlug) {
                    return null;
                  }

                  return (
                    <Link
                      key={relatedCampus.id}
                      href={`/${locale}/education/${encodeURIComponent(relatedSlug)}`}
                      className="d-flex justify-content-between align-items-center gap-2 p-2 rounded text-decoration-none"
                      style={{ background: "#f7fafc", border: "1px solid #e6eef5" }}
                    >
                      <span>
                        {relatedCampusName}
                        {relatedCampus.isPrimaryCampus ? ` • ${t("DetailPrimaryCampusSuffix")}` : ""}
                        {relatedLocation ? ` • ${relatedLocation}` : ""}
                      </span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!hasAnyDetailsSection && (
          <div className="card" style={{ borderRadius: 12 }}>
            <div className="card-body">
              <p className="mb-0 text-muted">{t("DetailLimitedPublicProfileMessage")}</p>
            </div>
          </div>
        )}

        {!centre.isClaimed && (
          <div
            className="card mt-4"
            style={{
              borderRadius: 12,
              border: "1px solid #dce6f1",
              boxShadow: "0 8px 24px rgba(15, 53, 87, 0.08)",
            }}
          >
            <div className="card-body d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div>
                <h2 className="h5 mb-2">{t("Title")}</h2>
                <p className="small text-muted mb-0">{t("ClaimBenefitsTrustStrip")}</p>
              </div>
              <Link href={claimHref} className="btn btn-primary">
                {t("DetailClaimThisCentreButton")}
              </Link>
            </div>
          </div>
        )}

        {!centre.isClaimed && (
          <div
            className="d-md-none"
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
              padding: "10px 12px calc(10px + env(safe-area-inset-bottom))",
              background: "rgba(255, 255, 255, 0.97)",
              borderTop: "1px solid #dce6f1",
              backdropFilter: "blur(4px)",
            }}
          >
            <Link href={claimHref} className="btn btn-primary w-100">
              {t("DetailClaimThisCentreButton")}
            </Link>
          </div>
        )}
        </div>
      </section>
    </>
  );
};

export default DetailMain;
